/* ============================================================================
   ATHLO+ — Booking widget (ADDON)
   ----------------------------------------------------------------------------
   Storefront client for the Supabase `bookings` edge function. Mounts into
   [data-booking] (sections/hub-booking.liquid), styled by assets/hub-booking.css.

   THE FLOW, and why it is this shape:
     1. GET  ?action=services&partner=   -> what can be booked, and which Shopify
                                            variant each service maps to.
     2. GET  ?action=availability        -> GENERATED slots. Never cached as slots
                                            server-side, so never cached here either.
     3. Customer picks UP TO 3 times, in preference order. Asking a partner
        "which of these?" beats "yes or no?" and measurably reduces declines, so
        the UI ranks the picks (1st / 2nd / 3rd) rather than treating 2 and 3 as
        an afterthought.
     4. POST ?action=hold               -> reserves ONLY the 1st choice as
                                            occupancy (holding all three would
                                            over-reserve the partner).
     5. POST /cart/add.js WITH LINE ITEM PROPERTIES. This is the one irreversible
        step: `_booking_*` properties can only be set at add-to-cart and can
        NEVER be attached after the order exists. If the cart add fails we release
        the hold immediately, because otherwise we would be sitting on occupancy
        for a cart line that does not exist.

   GRACEFUL FAILURE IS THE POINT. If the function is unreachable, slow, or
   returns junk, we render "Request a session" with an email path. An empty
   calendar is indistinguishable from a broken one, so we never render one.

   Removable: delete this file, hub-booking.css and sections/hub-booking.liquid.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------------
     Contract mirrors. These MUST match supabase/functions/bookings/lib/types.ts.
     Duplicated rather than imported because the theme has no build step; if the
     contract changes, change it here too.
     ------------------------------------------------------------------------- */
  var MAX_CANDIDATES = 3;                       // types.ts MAX_CANDIDATES

  var LI = {                                    // types.ts LI - line item property keys
    HOLD_ID: '_booking_hold_id',
    HOLD_TOKEN: '_booking_hold_token',
    STARTS_AT: '_starts_at',
    PARTNER_ID: '_partner_id',
    SERVICE_ID: '_service_id',
    REQUEST_ID: '_request_id'
  };

  // NOT in the LI const, and deliberately so: the contract has no channel for
  // candidates 2 and 3 or the flexible note, but line item properties are the
  // only thing that survives to the order and they can only be set right here.
  // We send the same data in the ?action=hold body (the intended path); these
  // extra hidden props are the recovery copy if the hold route ignores it.
  // Underscore-prefixed, so they stay out of the cart/checkout UI.
  var LI_X = {
    CANDIDATES: '_booking_candidates',
    FLEXIBLE: '_booking_flexible',
    // The customer's WhatsApp number, for partners who work on WhatsApp (BikeCraft One). Sent in
    // the ?action=hold body too, but the body only reaches the guardrail screen - jobs.ts stamps
    // booking_requests.customer_phone from the ORDER, and a service checkout collects an email and
    // usually nothing else (booking products are requiresShipping:false on purpose). So this
    // property is the channel that actually reaches the partner's confirmation mail.
    WHATSAPP: '_booking_whatsapp'
  };

  // Every ERR code in types.ts gets human copy. `slot_taken` is deliberately
  // written as a normal event, not a failure - it is the expected outcome of two
  // people wanting the same 7pm and it must not read like the customer broke something.
  var ERR_COPY = {
    bad_input: 'Something in that request didn’t look right. Pick your times again and resend.',
    not_found: 'We can’t find that session any more — it may have just been taken off the schedule.',
    forbidden: 'That request isn’t valid any more. Reload the page and try again.',
    rate_limited: 'That was a lot of tries at once. Give it a minute, then go again.',
    slot_taken: 'Someone booked that time a moment before you. Nothing was charged — here are the times that are still open.',
    hold_expired: 'Your hold ran out, so the time went back on the board. Nothing was charged — pick again.',
    partner_paused: 'This {noun} isn’t taking new bookings right now.',
    outside_window: 'That time is either too soon or too far ahead to book. Pick another.',
    already_resolved: 'This request has already been answered.',
    upstream_failed: 'Their calendar didn’t answer, so we can’t confirm instantly. Send it as a request and we’ll sort it by hand.',
    // A blocked customer is told the booking cannot go through and given a human
    // to talk to. DELIBERATELY not "you are banned": naming the rule tells someone
    // gaming it exactly what to change, and on the rare false positive it accuses
    // an innocent customer instead of opening a conversation.
    blocked: 'We can’t put this booking through online. Email us and we’ll take it from there.',
    // This one HAS an action attached, so the copy names it. Anything vaguer would
    // read as a fault and send an honest customer to support for nothing.
    too_many_open: 'You’ve got a few sessions already booked. Cancel or complete one and you can book this — or email us if you need more at once.',
    // The partner's OWN "form before booking" gate (Forged's VO2max, Lactate, HYROX, etc). The hold
    // returns this when the customer has not completed the partner's hosted screening form for THIS
    // email yet. It names the exact action, because it is entirely fixable by the customer, and it is
    // NOT a fault - nothing was charged and the slot was not taken.
    intake_required: 'This {noun} needs a short screening form completed first. Open it, finish it using the same email you gave us, then try again — nothing was charged.',
    internal: 'That broke on our end, not yours. Try again — or send it as a request and we’ll confirm by hand.'
  };

  var STORE_KEY = 'athlo_booking_hold';   // sessionStorage: survives a reload, not a new tab
  var FETCH_MS = 9000;                    // give up on the backend and show the fallback
  var REFETCH_GAP_MS = 1200;              // coalesce availability refetches (be kind to the rate limiter)
  var REVALIDATE_MS = 40000;              // proactive availability re-read while the form sits open
  var KEY_LOCALE = 'en-GB';               // date KEYS only: Gregorian, latin digits, locale-proof
  var LOCALE = (document.documentElement.getAttribute('lang') || 'en-SG');

  /* --------------------------------------------------------------------------
     PROACTIVE REVALIDATION registry.
     The calendar a customer browses is a cached hint; the slot is re-checked
     LIVE against the partner's calendar at hold time, so nobody is ever charged
     for a taken slot. But finding that out at the END of the form (fill it in,
     hit book, get "someone just took it") is friction that loses a booking. So
     every open widget also re-reads availability in the background - on a gentle
     timer and, more importantly, the instant the tab regains focus - and a slot
     that has since filled greys out BEFORE the form is filled, not after.

     One global visibility/focus listener drives every widget on the page (rather
     than one listener per widget, which would leak on the theme editor's repeated
     section re-inits). Detached widgets are pruned on every fire.
     ------------------------------------------------------------------------- */
  var WIDGETS = [];
  var GLOBAL_BOUND = false;
  function fireRevalidation(reason) {
    if (document.hidden) return;
    WIDGETS = WIDGETS.filter(function (w) { return document.contains(w.root); });
    WIDGETS.forEach(function (w) { try { w.reval(reason); } catch (e) { /* one widget must not break the rest */ } });
  }
  function registerWidget(root, reval) {
    // Replace any prior registration for this same root (theme-editor re-init) and drop dead ones.
    WIDGETS = WIDGETS.filter(function (w) { return w.root !== root && document.contains(w.root); });
    WIDGETS.push({ root: root, reval: reval });
    if (GLOBAL_BOUND) return;
    GLOBAL_BOUND = true;
    document.addEventListener('visibilitychange', function () { if (!document.hidden) fireRevalidation('visible'); });
    window.addEventListener('focus', function () { fireRevalidation('focus'); });
  }

  /* ---------------------------------------------------------------------------
     Small helpers
     ------------------------------------------------------------------------- */
  function el(tag, cls, txt) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (txt != null) e.textContent = txt;     // textContent everywhere: no server string is ever parsed as HTML
    return e;
  }
  function clear(node) { while (node && node.firstChild) node.removeChild(node.firstChild); }
  function track(name, params) { try { if (window.HubTrack) window.HubTrack(name, params || {}); } catch (e) { /* analytics is never load-bearing */ } }
  function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || '').trim()); }
  function digits(v) { return String(v || '').replace(/[^0-9]/g, ''); }
  // Deliberately the SAME test as jobs.ts phoneish(): at least 7 digits, format free. Anything
  // stricter rejects real numbers (numbering plans differ per country and people paste +65, 65 and
  // bare local forms interchangeably), and the only job here is to stop an empty box or a typed
  // sentence reaching a partner as the number they are meant to call.
  function validPhone(v) { return digits(v).length >= 7; }

  function store(v) {
    try {
      if (v === null) sessionStorage.removeItem(STORE_KEY);
      else sessionStorage.setItem(STORE_KEY, JSON.stringify(v));
    } catch (e) { /* Safari private mode: the hold still exists server-side and expires on its own */ }
  }
  function stored() {
    try { return JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null'); } catch (e) { return null; }
  }

  function apiErr(code, msg) {
    var e = new Error(msg || code || 'internal');
    e.code = code || 'internal';
    return e;
  }

  /* ---------------------------------------------------------------------------
     One widget instance
     ------------------------------------------------------------------------- */
  function init(scope) {
    var roots = (scope || document).querySelectorAll ? (scope || document).querySelectorAll('[data-booking]') : [];
    Array.prototype.forEach.call(roots, function (root) {
      if (root.getAttribute('data-hb-ready') === '1') return;   // idempotent: shopify:section:load re-fires init
      root.setAttribute('data-hb-ready', '1');
      try { boot(root); } catch (e) { hardFallback(root); }
    });
  }

  // Last-resort: something threw before the widget could build itself. Still show
  // a route to a human rather than a dead box.
  function hardFallback(root) {
    try {
      var cfg = readConfig(root) || {};
      clear(root);
      var card = el('div', 'hb__card');
      card.appendChild(el('p', 'hb__fb-h', 'Request a session'));
      card.appendChild(el('p', 'hb__fb-p', 'Our booking calendar didn’t load. Email us the session you want and we’ll come back with times.'));
      if (cfg.email) {
        var a = el('a', 'hb__link', 'Email us');
        a.href = 'mailto:' + cfg.email;
        card.appendChild(a);
      }
      root.appendChild(card);
    } catch (e) { /* nothing left to do */ }
  }

  function readConfig(root) {
    var tag = root.querySelector('script[type="application/json"][data-booking-config]');
    if (!tag) return null;
    try { return JSON.parse(tag.textContent || '{}'); } catch (e) { return null; }
  }

  function boot(root) {
    var CFG = readConfig(root);
    if (!CFG) { hardFallback(root); return; }

    var sid = CFG.sid || Math.random().toString(36).slice(2, 8);
    // What to call the person/place on the other end. A massage therapist, a test lab and a
    // bike workshop are not "your coach"; the section setting names them and every string below
    // reads from it. Default 'provider' is the safe neutral.
    var NOUN = String(CFG.noun || 'provider').trim() || 'provider';
    function noun(t) { return String(t).replace(/\{noun\}/g, NOUN); }
    // Strict === true, not truthy: an absent key, a null from `| json` on an unset Liquid setting,
    // or the string "false" must all leave the field optional. Making a contact field mandatory is
    // the kind of change that must require someone to have deliberately asked for it.
    var WA_REQ = CFG.waRequired === true;
    // A plain REQUIRED MOBILE (2026-10-06, owner: every service partner should get the customer's number,
    // "it makes it easier for all the partners"). Same strict === true rule. WhatsApp-required implies
    // it; this is the same requirement without the WhatsApp wording, for partners who just call.
    var PH_REQ = WA_REQ || CFG.phoneRequired === true;
    // App Proxy first when configured: same-origin, Shopify-signed, and the
    // customer identity comes from the signed request rather than the body.
    // Falls back to the direct function URL (public routes are CORS + rate limited).
    var BASE = (CFG.proxy || '').trim() || (CFG.endpoint || '').trim();
    var LITE = !!window.HUB_LITE;
    // Lite / data-saver clients fetch a shorter window up front; "More dates"
    // extends it on demand instead of pulling 30 days over a 2G link.
    var WINDOW_DAYS = Math.max(1, parseInt(CFG.days, 10) || 14);
    if (LITE) WINDOW_DAYS = Math.min(WINDOW_DAYS, 7);

    var S = {
      services: [], service: null,
      slots: [], byDate: {}, dateKeys: [],
      dateKey: null, picks: [],
      // True when the partner books INSTANTLY (Wix / native auto-confirm): the first pick is placed on
      // their calendar the moment the customer pays, so the widget offers ONE time rather than "propose
      // up to three and they confirm one". Set from the services response in load().
      instant: false,
      windowDays: WINDOW_DAYS, maxDays: 0,
      // True when the widget is on ONE test's own product page (see load): the full session chooser is
      // trimmed to just that test, since listing the partner's whole catalogue on a single-test page is
      // noise. The machine selector is independent of this.
      pinned: false,
      hold: null, lineKey: null,
      // The current service's intake-form gate: { required, url }. Populated per service by
      // fetchIntake(); null until then. A gated service (Forged's VO2max/Lactate/HYROX/etc) sends the
      // customer to the PARTNER'S OWN hosted form before booking - we never recreate it.
      intake: null,
      // Has the customer actually OPENED the partner's screening form? The confirm box (and so the whole
      // booking) stays locked until they have, so nobody can tick past a form they never saw. Reset when
      // the TEST changes (a different test is a different form).
      screeningOpened: false,
      busy: false, tick: null, lastFetch: 0, warned: {},
      // mode: which screen is showing (form|hold|fallback), so background revalidation
      // only ever repaints the live calendar. reval: the proactive re-read timer handle.
      mode: null, reval: null
    };

    /* ---- formatters (partner timezone, falling back to the viewer's) --------
       Rules are wall-clock in the PARTNER's timezone, so a session at "Tue 7pm"
       must read as 7pm even for a viewer in another zone. An invalid IANA name
       throws RangeError on construction, so every formatter is guarded. */
    var TZ = (CFG.tz || '').trim() || null;
    function fmtr(locale, opts) {
      var o = {};
      for (var k in opts) o[k] = opts[k];
      if (TZ) o.timeZone = TZ;
      try { return new Intl.DateTimeFormat(locale, o); }
      catch (e) { delete o.timeZone; try { return new Intl.DateTimeFormat(locale, o); } catch (e2) { return null; } }
    }
    var F_KEY = fmtr(KEY_LOCALE, { year: 'numeric', month: '2-digit', day: '2-digit' });
    var F_TIME = fmtr(LOCALE, { hour: 'numeric', minute: '2-digit' });
    var F_DOW = fmtr(LOCALE, { weekday: 'short' });
    var F_DAY = fmtr(LOCALE, { day: 'numeric' });
    var F_MON = fmtr(LOCALE, { month: 'short' });
    var F_FULL = fmtr(LOCALE, { weekday: 'long', day: 'numeric', month: 'long' });
    var VIEWER_TZ = '';
    try { VIEWER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { VIEWER_TZ = ''; }

    function dkey(d) {
      if (!F_KEY) return d.toISOString().slice(0, 10);
      var p = F_KEY.formatToParts(d), o = {};
      for (var i = 0; i < p.length; i++) o[p[i].type] = p[i].value;
      return o.year + '-' + o.month + '-' + o.day;
    }
    function fmt(f, d, fb) { try { return f ? f.format(d) : fb; } catch (e) { return fb; } }
    function timeOf(iso) { return fmt(F_TIME, new Date(iso), String(iso).slice(11, 16)); }
    function fullOf(iso) { return fmt(F_FULL, new Date(iso), String(iso).slice(0, 10)); }
    function whenOf(iso) { return fullOf(iso) + ', ' + timeOf(iso); }

    function money(cents) {
      var n = (parseInt(cents, 10) || 0) / 100;
      try { return new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CFG.currency || 'SGD' }).format(n); }
      catch (e) { return (CFG.currency || 'SGD') + ' ' + n.toFixed(2); }
    }

    /* ---- DOM skeleton -----------------------------------------------------
       Built in JS (not Liquid) so there is exactly one place that decides which
       of the three modes - form / hold / fallback - is on screen. */
    clear(root);
    var card = el('div', 'hb__card');
    root.appendChild(card);

    var live = el('div', 'hb__live sr-only');
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('aria-atomic', 'true');
    root.appendChild(live);
    function announce(msg) {
      live.textContent = '';
      setTimeout(function () { live.textContent = msg; }, 60);   // reset first so identical repeats re-announce
    }

    var body = el('div', 'hb__body');
    card.appendChild(body);

    /* ---- backend calls ---------------------------------------------------- */
    function api(action, opts) {
      opts = opts || {};
      if (!BASE) return Promise.reject(apiErr('internal', 'no endpoint configured'));
      var url = BASE + (BASE.indexOf('?') > -1 ? '&' : '?') + 'action=' + encodeURIComponent(action);
      if (opts.query) {
        for (var k in opts.query) {
          if (opts.query[k] == null || opts.query[k] === '') continue;
          url += '&' + encodeURIComponent(k) + '=' + encodeURIComponent(opts.query[k]);
        }
      }
      var init2 = { method: opts.method || 'GET', headers: { Accept: 'application/json' }, credentials: 'same-origin' };
      if (opts.body) {
        init2.headers['Content-Type'] = 'application/json';
        init2.body = JSON.stringify(opts.body);
      }
      // A hung backend is worse than a down one: without a timeout the widget
      // shows a skeleton forever and looks broken with no way out.
      var ctl = null, timer = null;
      try { ctl = new AbortController(); init2.signal = ctl.signal; } catch (e) { ctl = null; }
      if (ctl) timer = setTimeout(function () { try { ctl.abort(); } catch (e) {} }, FETCH_MS);

      return fetch(url, init2).then(function (r) {
        if (timer) clearTimeout(timer);
        return r.text().then(function (txt) {
          var json = null;
          try { json = JSON.parse(txt); } catch (e) { json = null; }
          if (json === null) {
            // Not JSON at all - a proxy error page, a 502, an HTML challenge.
            // Map the status so the customer gets useful copy, not "undefined".
            if (r.status === 429) throw apiErr('rate_limited');
            if (r.status === 404) throw apiErr('not_found');
            throw apiErr('internal', 'HTTP ' + r.status);
          }
          if (json.ok === false) throw apiErr(json.error || 'internal', json.message);
          if (json.ok === true) return json.data;
          // Defensive: accept a bare payload so a backend (or a proxy that
          // unwraps the envelope) still renders instead of hard-failing.
          if (Array.isArray(json) || typeof json === 'object') return json;
          throw apiErr('internal', 'unreadable response');
        });
      }, function (e) {
        if (timer) clearTimeout(timer);
        // AbortError / TypeError(network) both mean "backend unreachable".
        throw apiErr('offline', (e && e.message) || 'network');
      });
    }

    /* ---- mode switching -------------------------------------------------- */
    function setMode(mode, arg) {
      S.mode = mode;               // read by maybeRevalidate() so background re-reads only touch the form
      stopClock();                 // never leave a ticker painting into detached DOM
      clear(body);
      if (mode === 'fallback') return renderFallback(arg);
      if (mode === 'hold') return renderHold();
      renderForm();
    }

    /* =======================================================================
       FALLBACK: "Request a session". This is the state the project's handover
       notes call out as a standing gap - a widget that renders nothing when its
       backend is down. Every failure path lands here with a working email path.
       ===================================================================== */
    function renderFallback(reason) {
      var wrap = el('div', 'hb__fb');
      var heading = reason === 'flexible' ? 'Send your flexible request' : (CFG.fbHeading || 'Request a session');
      wrap.appendChild(el('h3', 'hb__fb-h', heading));
      var why;
      if (reason === 'flexible') {
        // Not a failure: "I'm flexible" with no concrete time cannot become a
        // hold, so it goes to a human with everything already filled in.
        why = 'We’ve put your note together with the session details. Send it over and we’ll come back with times that fit — no payment until a time is agreed.';
      } else if (reason === 'empty') {
        why = CFG.fbEmpty || 'There are no open times on the calendar right now. Tell us when you want to train and we’ll find you a slot.';
      } else if (reason === 'partner_paused') {
        why = noun('This {noun} has paused new bookings. Send us a note and we’ll tell you the moment they reopen — or put you with someone similar.');
      } else {
        why = CFG.fbText || 'Our live calendar isn’t answering right now. Send us the session you want — name your preferred days and times — and we’ll come back with what’s open. Same coach, same price, just one extra message.';
      }
      wrap.appendChild(el('p', 'hb__fb-p', why));

      var actions = el('div', 'hb__fb-actions');
      if (CFG.email) {
        var a = el('a', 'hb__link', 'Email your request');
        a.href = mailto();
        a.addEventListener('click', function () { track('booking_fallback_email', { reason: reason || 'offline' }); });
        actions.appendChild(a);
      }
      if (CFG.phone) {
        var w = el('a', 'hb__link hb__link--ghost', 'Message on WhatsApp');
        w.href = 'https://wa.me/' + digits(CFG.phone) + '?text=' + encodeURIComponent(requestText());
        w.target = '_blank';
        w.rel = 'noopener';
        w.addEventListener('click', function () { track('booking_fallback_whatsapp', { reason: reason || 'offline' }); });
        actions.appendChild(w);
      }
      // Retry only makes sense when the backend was the problem, not when the
      // calendar genuinely has no open times.
      if (reason !== 'empty' && reason !== 'unconfigured') {
        var retry = el('button', 'hb__btn', reason === 'flexible' ? 'Back to the calendar' : 'Try the calendar again');
        retry.type = 'button';
        retry.addEventListener('click', function () {
          retry.disabled = true;
          if (reason === 'flexible' && S.service) { setMode('form'); return; }   // data is still in memory
          load();
        });
        actions.appendChild(retry);
      }
      wrap.appendChild(actions);

      if (reason === 'empty' && S.maxDays > S.windowDays) {
        var more = el('button', 'hb__btn', 'Look further ahead');
        more.type = 'button';
        more.style.marginTop = '0.6rem';
        more.addEventListener('click', function () { more.disabled = true; extend(); });
        wrap.appendChild(more);
      }

      /* THE WAITLIST. This is the exact moment it exists for: the customer came
         to book, there is nothing open, and until now the only outcomes were
         "email us" or "leave". Someone who leaves here is unreachable forever,
         and they are the person most likely to take a slot the moment one frees.
         The backend sweep does the rest - see lib/guardrails.ts waitlistSweep. */
      if (reason === 'empty' && S.service) wrap.appendChild(renderWaitlist());
      // Merchant-only hint. Never shown to a shopper - in the live storefront
      // they just get the request path, which is the whole point.
      if (reason === 'unconfigured' && window.Shopify && window.Shopify.designMode) {
        wrap.appendChild(el('p', 'hb__fb-why', 'Theme editor: set the partner handle (and endpoint) in this section’s settings.'));
      }
      body.appendChild(wrap);
      announce(why);
      track('booking_fallback', { reason: reason || 'offline', partner: CFG.partner || '' });
    }

    /* =======================================================================
       THE WAITLIST BLOCK
       -----------------------------------------------------------------------
       Deliberately COLLAPSED behind one button rather than shown as a form.
       This block appears at the bottom of a failure state, and a bare email
       field there reads as "give us your address" - a second ask, from a page
       that has just told you it has nothing. One button that says exactly what
       it does earns the field.

       THE WINDOW is the one the customer is already looking at (now -> the end
       of their current view, extended by "Look further ahead"). Asking them to
       pick a date range would be a third ask, and the range they want is the
       one on screen: the sweep only tells them about slots inside it.

       ONE HONESTY RULE, and it matches the email the backend sends: the slot is
       never held. Say so here, before they hand over an address, rather than
       only in the mail that arrives after somebody else has taken it.
       ===================================================================== */
    function renderWaitlist(opts) {
      opts = opts || {};
      var box = el('div', 'hb__wl');
      box.style.marginTop = '1rem';

      var btn = el('button', 'hb__btn', opts.label || 'Tell me when a time opens');
      btn.type = 'button';
      box.appendChild(btn);

      var form = el('div', 'hb__wl-form');
      form.hidden = true;
      form.style.marginTop = '0.6rem';

      var lab = el('label', 'hb__wl-lab', 'Email');
      var input = el('input', 'hb__input');
      input.type = 'email';
      input.autocomplete = 'email';
      input.placeholder = 'you@example.com';
      // Programmatic association, not just proximity: this field is injected at
      // runtime into a block a screen-reader user reached by following an error.
      var fid = 'hb-wl-' + Math.random().toString(36).slice(2, 8);
      input.id = fid;
      lab.setAttribute('for', fid);
      form.appendChild(lab);
      form.appendChild(input);

      var note = el('p', 'hb__fb-why',
        'We’ll email you once, as soon as something opens in the dates you’re looking at. '
        + 'It won’t be held for you — first to book gets it.');
      form.appendChild(note);

      var go = el('button', 'hb__btn', 'Add me');
      go.type = 'button';
      go.style.marginTop = '0.4rem';
      form.appendChild(go);

      var msg = el('p', 'hb__fb-why');
      msg.setAttribute('role', 'status');
      form.appendChild(msg);

      btn.addEventListener('click', function () {
        btn.hidden = true;
        form.hidden = false;
        try { input.focus(); } catch (e) {}
        track('booking_waitlist_open', { partner: CFG.partner || '', service: S.service.id });
      });

      go.addEventListener('click', function () {
        var email = String(input.value || '').trim();
        if (!validEmail(email)) {
          input.setAttribute('aria-invalid', 'true');
          msg.textContent = 'That email doesn’t look right — it’s the only way we can reach you.';
          try { input.focus(); } catch (e) {}
          return;
        }
        input.removeAttribute('aria-invalid');
        go.disabled = true;
        msg.textContent = 'Adding you…';
        var now = Date.now();
        api('waitlist-join', {
          method: 'POST',
          body: {
            serviceId: S.service.id,
            email: email,
            customerName: String((UI.name && UI.name.value) || '').trim() || undefined,
            // The window on screen. maxDays is the service horizon, so this can
            // never ask the sweep about a slot the service would refuse anyway.
            // A window the customer actually chose beats one we inferred. From the per-day empty
            // state that is THAT DAY - a far better match than "sometime in the next 30 days", and
            // it means the sweep only mails them about a slot they would genuinely take.
            windowFrom: opts.from || new Date(now).toISOString(),
            windowTo: opts.to || new Date(now + (S.maxDays || S.windowDays || 30) * 86400000).toISOString()
          }
        }).then(function (r) {
          form.hidden = true;
          var done = el('p', 'hb__fb-p',
            'You’re on the list. We’ll email ' + email + ' the moment a time opens.');
          box.appendChild(done);
          announce('You are on the waitlist.');
          track('booking_waitlist_join', {
            partner: CFG.partner || '', service: S.service.id, ref: (r && r.ref) || ''
          });
        }).catch(function (e) {
          go.disabled = false;
          // 'blocked' must not be spelled out. Somebody on the block list gets
          // the same neutral line as a transient failure: telling them they are
          // banned tells them exactly what to change.
          var code = (e && e.code) || 'internal';
          msg.textContent = code === 'rate_limited'
            ? 'That was a lot of tries at once. Give it a minute.'
            : 'We couldn’t add you just now. Try the email link above and we’ll do it by hand.';
          track('booking_waitlist_error', { code: code });
        });
      });

      box.appendChild(form);
      return box;
    }

    // Everything we know, as plain text, for the email / WhatsApp path. The
    // customer should never have to retype what they already told the widget.
    function requestText() {
      var lines = [];
      lines.push('Session: ' + ((S.service && S.service.title) || CFG.serviceTitle || 'Coaching session'));
      if (CFG.partnerName || CFG.partner) lines.push('Coach / partner: ' + (CFG.partnerName || CFG.partner));
      if (S.picks.length) {
        lines.push('Preferred times:');
        S.picks.forEach(function (p, i) { lines.push('  ' + (i + 1) + '. ' + whenOf(p.starts_at)); });
      }
      var note = noteValue();
      if (note) lines.push('Flexible: ' + note);
      if (CFG.customerName) lines.push('Name: ' + CFG.customerName);
      if (CFG.customerEmail) lines.push('Email: ' + CFG.customerEmail);
      lines.push('');
      lines.push('Sent from ' + location.href);
      return lines.join('\n');
    }
    function mailto() {
      var subj = 'Session request — ' + ((S.service && S.service.title) || CFG.serviceTitle || (CFG.partnerName || CFG.partner || 'ATHLO+'));
      return 'mailto:' + CFG.email + '?subject=' + encodeURIComponent(subj) + '&body=' + encodeURIComponent(requestText());
    }

    /* =======================================================================
       FORM: service -> date -> times -> picks -> details -> submit
       ===================================================================== */
    var UI = {};

    function renderForm() {
      UI = {};

      /* -- service chooser (only when there is a real choice) -- */
      // ONE entry per test: ungrouped services plus each modality group's base. Machine variants are
      // NOT listed here - they are chosen in the machine selector below, once their test is picked.
      // On a PINNED page (this test's own product page) the whole list is trimmed to just this test, so
      // a customer on the VO2max page is not shown the partner's other nine services.
      var primaries = S.services.filter(isPrimaryService);
      if (primaries.length > 1 && !S.pinned) {
        var st = step('Choose your session');
        var grid = el('div', 'hb__svc');
        grid.setAttribute('role', 'group');
        grid.setAttribute('aria-label', 'Choose your session');
        primaries.forEach(function (svc) {
          var b = el('button', 'hb__svc-opt');
          b.type = 'button';
          // Pressed when this test is the current one - directly, or because a machine WITHIN its group
          // is what is selected right now.
          var inGroup = S.service && svc.modality_group && String(S.service.modality_group) === String(svc.modality_group);
          b.setAttribute('aria-pressed', (S.service && S.service.id === svc.id) || inGroup ? 'true' : 'false');
          b.appendChild(el('span', 'hb__svc-t', svc.title || 'Session'));
          var meta = (svc.duration_minutes ? svc.duration_minutes + ' min' : '');
          if (svc.price_cents != null) meta += (meta ? ' · ' : '') + money(svc.price_cents);
          b.appendChild(el('span', 'hb__svc-m', meta));
          b.addEventListener('click', function () { chooseService(svc); });
          grid.appendChild(b);
        });
        st.appendChild(grid);
      } else if (S.service) {
        // Single-test header (a pinned page, or a partner with one service). Name the TEST, not the
        // machine: in a group we show the base's title, since the machine is picked in the selector.
        var headSvc = S.service;
        if (S.service.modality_group) {
          var base = S.services.filter(function (s) { return String(s.id) === String(S.service.modality_group); })[0];
          if (base) headSvc = base;
        }
        var one = step(headSvc.title || 'Session');
        var meta2 = (headSvc.duration_minutes ? headSvc.duration_minutes + ' min' : '');
        // Price follows the SELECTED option, not the group's base: modality options can differ in price
        // (BikeCraft bike-type). For a single-price group S.service === headSvc, so nothing changes.
        var priceSvc = S.service || headSvc;
        if (priceSvc.price_cents != null) meta2 += (meta2 ? ' · ' : '') + money(priceSvc.price_cents);
        if (meta2) one.appendChild(el('p', 'hb__hint hb__hint--tight', meta2));
      }

      /* -- machine (modality) selector: the same test on different equipment -- */
      // Each machine is its OWN bookable service with its OWN Wix calendar, so picking one re-reads
      // availability for that machine (via chooseService), and the hold, the intake gate and the cart
      // line all follow it. Shown only when the chosen test genuinely has more than one machine, so a
      // single-machine test looks exactly as it did before.
      var machines = modalitySiblings(S.service);
      if (machines.length > 1) {
        // Show the price on each option ONLY when the group's options differ in price — e.g. BikeCraft's
        // Full Service is $290 (road/hardtail) vs $350 (full-suspension/TT bike). Forged's machine groups
        // all share one price, so this stays hidden there and the selector looks exactly as before.
        var modPrices = {};
        machines.forEach(function (m) { if (m.price_cents != null) modPrices[String(m.price_cents)] = 1; });
        var showModPrice = Object.keys(modPrices).length > 1;
        var ms = step(CFG.modalityLabel || 'Choose your machine');
        var mgrid = el('div', 'hb__svc hb__modes');
        mgrid.setAttribute('role', 'group');
        mgrid.setAttribute('aria-label', CFG.modalityLabel || 'Choose your machine');
        machines.forEach(function (svc) {
          var b = el('button', 'hb__svc-opt hb__mode-opt');
          b.type = 'button';
          b.setAttribute('aria-pressed', S.service && S.service.id === svc.id ? 'true' : 'false');
          b.appendChild(el('span', 'hb__svc-t', svc.modality_label || svc.title || 'Option'));
          if (showModPrice && svc.price_cents != null) b.appendChild(el('span', 'hb__svc-m', money(svc.price_cents)));
          b.addEventListener('click', function () { chooseService(svc); });
          mgrid.appendChild(b);
        });
        ms.appendChild(mgrid);
      }

      /* SCREENING — the partner's OWN "form before booking" gate, and now the FIRST thing on a gated
         test and the visual priority. Forged's VO2max/Lactate/HYROX/etc will not accept a booking until
         the customer has completed the partner's hosted form (a signed clinical waiver they own and are
         liable for - we never recreate it). Two things are enforced, not one: the confirm box is LOCKED
         until the customer has actually OPENED the form (so it cannot be ticked past a form they never
         saw), and the booking button then stays disabled until the box is ticked. The hold's own 428 is
         the authoritative backstop. Shown only when intake-link said this service is gated + gave a URL. */
      UI.screenBox = null;
      if (S.intake && S.intake.required && S.intake.url) {
        var opened = !!S.screeningOpened;
        var card = el('div', 'hb__screen' + (opened ? ' is-open' : ''));

        var head = el('div', 'hb__screen-head');
        head.appendChild(el('span', 'hb__screen-badge', '1'));
        head.appendChild(el('span', 'hb__screen-ttl', CFG.screeningIntro || noun('Quick screening — do this first')));
        card.appendChild(head);
        card.appendChild(el('p', 'hb__screen-body',
          CFG.screeningBody || noun('This {noun} needs a short screening form completed before your first session. It opens in a new tab — use the same email you book with here.')));

        // The open control is a real link (new tab, rel=noopener) AND the unlock: clicking it opens the
        // partner's form and enables the confirm. Required, never optional.
        var open = el('a', 'hb__screen-open');
        open.href = S.intake.url;
        open.target = '_blank';
        open.rel = 'noopener noreferrer';
        var openT = el('span', 'hb__screen-open-t', opened ? (CFG.screeningReopen || 'Reopen the form') : (CFG.screeningCta || 'Open the screening form'));
        open.appendChild(openT);
        open.appendChild(el('span', 'hb__screen-open-i', '↗'));
        card.appendChild(open);

        var srow = el('label', 'hb__screen-confirm' + (opened ? '' : ' is-locked'));
        var scb = el('input');
        scb.type = 'checkbox';
        scb.id = 'hb-screen-' + sid;
        scb.disabled = !opened;
        scb.addEventListener('change', paintCta);
        srow.appendChild(scb);
        srow.appendChild(el('span', 'hb__screen-confirm-t', CFG.screeningConfirm || 'I’ve completed the screening form'));
        card.appendChild(srow);

        var hint = el('p', 'hb__screen-hint',
          opened ? (CFG.screeningDoneHint || 'Tick the box once you’ve finished the form.')
                 : (CFG.screeningLockHint || 'Open the form to continue — the box unlocks after.'));
        card.appendChild(hint);

        open.addEventListener('click', function () {
          S.screeningOpened = true;
          track('booking_intake_open', { partner: CFG.partner || '', service: S.service.id });
          card.className = 'hb__screen is-open';
          srow.className = 'hb__screen-confirm';
          scb.disabled = false;
          openT.textContent = CFG.screeningReopen || 'Reopen the form';
          hint.textContent = CFG.screeningDoneHint || 'Tick the box once you’ve finished the form.';
          paintCta();
        });

        body.appendChild(card);
        UI.screenBox = scb;
      }

      /* WORKSHOP (BikeCraft): no time-picking. The booked slot is a placeholder settled on WhatsApp,
         so the whole date/time UI is skipped and group() auto-holds the earliest open slot. The
         customer only leaves a WhatsApp number and pays; the workshop arranges drop-off/pickup. */
      if (S.workshop) {
        var wnote = step(CFG.workshopTitle || 'Instant booking');
        wnote.appendChild(el('p', 'hb__hint', CFG.workshopHint
          || 'No need to pick a time — the workshop arranges your drop-off or pickup with you on WhatsApp. Leave your number below and pay, and your bike is in the queue the moment you do.'));
      }

      /* -- dates: ARIA Tabs. Days with no times stay VISIBLE and selectable so
            a full week reads as "fully booked", not as a calendar that failed.
            Skipped for a workshop partner (guarded above): they never pick a time. -- */
      if (!S.workshop) {
      var ds = step('Choose a date');
      UI.dates = el('div', 'hb__dates');
      UI.dates.setAttribute('role', 'tablist');
      UI.dates.setAttribute('aria-label', 'Choose a date');
      UI.dates.addEventListener('keydown', onDateKeys);
      ds.appendChild(UI.dates);
      if (TZ && VIEWER_TZ && TZ !== VIEWER_TZ) {
        ds.appendChild(el('p', 'hb__tz', 'Times shown in ' + TZ.replace(/_/g, ' ')));
      }

      /* -- times -- */
      var ts = el('div', 'hb__step');
      UI.timesLabel = el('p', 'hb__label');
      if (S.instant) {
        // One time, booked on the spot. No ranked list, no "0 of 3" counter.
        UI.timesLabel.appendChild(document.createTextNode('Pick your time'));
        UI.count = null;
      } else {
        UI.timesLabel.appendChild(document.createTextNode('Pick up to ' + MAX_CANDIDATES + ' times '));
        UI.count = el('b', null, '0 of ' + MAX_CANDIDATES);
        UI.timesLabel.appendChild(UI.count);
      }
      ts.appendChild(UI.timesLabel);
      ts.appendChild(el('p', 'hb__hint', S.instant
        ? (CFG.pickHintInstant || 'Confirmed instantly — your slot is booked the moment you pay.')
        : (CFG.pickHint || noun('Give your {noun} a choice and they can say yes to the one that fits. One time works too.'))));
      UI.times = el('div', 'hb__times');
      UI.times.setAttribute('role', 'tabpanel');
      UI.times.addEventListener('keydown', onTimeKeys);
      ts.appendChild(UI.times);
      UI.picks = el('ol', 'hb__picks');
      ts.appendChild(UI.picks);
      UI.odds = el('p', 'hb__odds');
      UI.odds.hidden = true;
      ts.appendChild(UI.odds);
      UI.more = el('button', 'hb__btn', 'More dates');
      UI.more.type = 'button';
      UI.more.style.marginTop = '0.7rem';
      UI.more.hidden = true;
      UI.more.addEventListener('click', extend);
      ts.appendChild(UI.more);
      body.appendChild(ts);
      } // end if (!S.workshop) — the date/time picker

      /* -- flexible: request-style partners only. An instant partner books a real slot on the spot, so
         "tell them roughly when suits and let them propose a time" has nobody to propose it. -- */
      if (!S.instant) {
      var fs = step('Can’t see a time that works?');
      var lab = el('label', 'hb__check');
      UI.flex = document.createElement('input');
      UI.flex.type = 'checkbox';
      UI.flex.id = 'hb-flex-' + sid;
      lab.appendChild(UI.flex);
      lab.appendChild(el('span', null, CFG.flexLabel || noun('I’m flexible — tell the {noun} roughly when suits and let them propose a time.')));
      fs.appendChild(lab);
      UI.noteWrap = el('label', 'hb__field hb__field--wide');
      UI.noteWrap.hidden = true;
      UI.noteWrap.appendChild(el('span', null, 'When suits you'));
      UI.note = document.createElement('textarea');
      UI.note.rows = 2;
      UI.note.maxLength = 240;
      UI.note.placeholder = 'e.g. weekday mornings before 9am, or any Saturday';
      UI.noteWrap.appendChild(UI.note);
      fs.appendChild(UI.noteWrap);
      UI.flex.addEventListener('change', function () {
        UI.noteWrap.hidden = !UI.flex.checked;
        if (UI.flex.checked) { try { UI.note.focus(); } catch (e) {} }
        paintCta();
      });
      }

      /* -- contact. Email is required: it is how the confirmation, the .ics and
            the refund notice reach them, and it is NOT NULL on booking_requests. */
      var cs = step('Your details');
      /* SIGNED OUT? SAY WHAT IT COSTS THEM, ONCE, HERE.
         A guest checkout is a perfectly good booking - the confirmation, the .ics, the tracker and
         the automatic refund all work from the email alone, and nothing below blocks them. What a
         guest silently forfeits is everything that needs an account: Points (loyalty awards on the
         paid order and skips an order with no customer id), and the session appearing in their
         locker with the Cancel and Reschedule buttons on it. Nobody was ever told that at the one
         moment they could still do something about it. The number is the FLOOR, not a promise: it
         is the 1x rate, and a member on a higher tier earns more. */
      if (!CFG.customerEmail) {
        var pts = (S.service && S.service.price_cents != null)
          ? Math.floor(Number(S.service.price_cents) / 100) : 0;
        var nudge = el('p', 'hb__fine hb__signin');
        nudge.appendChild(document.createTextNode(
          pts > 0
            ? 'Booking as a guest. Sign in first and this earns you at least ' + pts +
              ' Points, and lands in your locker where you can move or cancel it yourself. '
            : 'Booking as a guest. Sign in first and this lands in your locker with Points, where you can move or cancel it yourself. '
        ));
        var a = el('a', 'hb__link hb__link--ghost', 'Sign in');
        try {
          a.href = '/account/login?return_url=' + encodeURIComponent(location.pathname + location.search);
        } catch (e) { a.href = '/account/login'; }
        nudge.appendChild(a);
        cs.appendChild(nudge);
      }
      /* WAIVER. Three acknowledgements, each a required tick, collected BEFORE the slot is held.
         They ride to the order as line item properties under the SAME names the product-options app
         used ("Age", "Health Declaration", "Acknowledgment"), so an order reads exactly as it always
         did downstream - the difference is that a session booked through the picker now has one at
         all. Before 2026-08-02 the app collected these on the ordinary add-to-cart form, which this
         flow never touches: booking through the picker captured NO waiver, and buying through the
         app's button captured a waiver but created NO booking. */
      UI.waiver = [];
      var wlist = (CFG.waiverOn !== false && Array.isArray(CFG.waiver)) ? CFG.waiver : [];
      wlist = wlist.filter(function (w) { return w && w.text; });
      if (wlist.length) {
        var ws = step(CFG.waiverIntro || 'Before we hold your slot, please confirm:');
        var wbox = el('div', 'hb__waiver');
        wlist.forEach(function (w, i) {
          var row = el('label', 'hb__wrow');
          var cb = el('input');
          cb.type = 'checkbox';
          cb.id = 'hb-w-' + sid + '-' + i;
          cb.addEventListener('change', paintCta);
          row.appendChild(cb);
          row.appendChild(el('span', 'hb__wtext', w.text));
          wbox.appendChild(row);
          UI.waiver.push({ name: w.name || ('Acknowledgment ' + (i + 1)), text: w.text, box: cb });
        });
        ws.appendChild(wbox);
      }

      var fields = el('div', 'hb__fields');
      UI.email = field(fields, 'Email', 'email', CFG.customerEmail || '', true);
      UI.name = field(fields, 'Name', 'text', CFG.customerName || '', false);
      // WHATSAPP-FIRST PARTNERS ASK FOR THE NUMBER, AND MEAN IT.
      //
      // Default OFF, so every partner that does not set it keeps the optional "Mobile" box exactly
      // as it was. When on, this is the ONE place in the whole flow where something can actually be
      // made compulsory: after checkout Shopify owns the page and we can prompt but not require.
      // Asking here also inverts the handoff - with a number on the order the partner can open the
      // chat themselves instead of waiting to be messaged.
      UI.phone = field(fields,
        WA_REQ ? 'WhatsApp number' : (PH_REQ ? 'Mobile number' : 'Mobile (optional)'),
        'tel', CFG.customerPhone || '', PH_REQ);
      UI.phone.wrap.className = 'hb__field hb__field--wide';
      if (WA_REQ) {
        // A SIBLING of the label, not a child of it. `.hb__field > span` is the styled uppercase
        // caption, so a second span inside the wrap would render as a second label AND get folded
        // into the input's accessible name - the field would announce as "WhatsApp number the
        // workshop confirms your time on WhatsApp...". aria-describedby says it once, after the name.
        var hintId = 'hb-wa-' + sid;
        var hint = el('p', 'hb__fhint',
          CFG.waHint || noun('The {noun} confirms your time on WhatsApp, so this has to be a number they can reach you on.'));
        hint.id = hintId;
        fields.appendChild(hint);
        UI.phone.input.setAttribute('aria-describedby', hintId);
      } else if (PH_REQ) {
        var phHintId = 'hb-ph-' + sid;
        var phHint = el('p', 'hb__fhint', noun('Your {noun} uses it, with your email, to reach you about this booking.'));
        phHint.id = phHintId;
        fields.appendChild(phHint);
        UI.phone.input.setAttribute('aria-describedby', phHintId);
      }
      cs.appendChild(fields);

      /* -- submit -- */
      UI.go = el('button', 'hb__go', 'Pick a time');
      UI.go.type = 'button';
      UI.go.addEventListener('click', submit);
      body.appendChild(UI.go);
      body.appendChild(el('p', 'hb__fine', CFG.fine || ''));

      UI.msg = el('div', 'hb__msg');
      UI.msg.hidden = true;
      body.appendChild(UI.msg);

      renderDates();
      renderTimes();
      renderPicks();
    }

    function step(labelText) {
      var s = el('div', 'hb__step');
      if (labelText) s.appendChild(el('p', 'hb__label', labelText));
      body.appendChild(s);
      return s;
    }

    function field(parent, label, type, value, required) {
      var wrap = el('label', 'hb__field');
      wrap.appendChild(el('span', null, label));
      var input = document.createElement('input');
      input.type = type;
      input.value = value || '';
      if (type === 'email') { input.autocomplete = 'email'; input.inputMode = 'email'; input.placeholder = 'you@email.com'; }
      if (type === 'tel') { input.autocomplete = 'tel'; input.inputMode = 'tel'; }
      if (type === 'text') input.autocomplete = 'name';
      if (required) input.required = true;
      wrap.appendChild(input);
      parent.appendChild(wrap);
      var o = { wrap: wrap, input: input };
      Object.defineProperty(o, 'value', { get: function () { return input.value; } });
      return o;
    }

    // The message box is deliberately NOT its own live region. One polite region
    // (announce) speaks everything, so an error is never read out twice - once by
    // role="alert" and once by the announcer - which is what happens when both
    // exist and is genuinely unpleasant with a screen reader.
    function say(msg, kind) {
      if (!UI.msg) { announce(msg); return; }
      UI.msg.className = 'hb__msg' + (kind === 'bad' ? ' hb__msg--bad' : kind === 'ok' ? ' hb__msg--ok' : '');
      UI.msg.textContent = msg;
      UI.msg.hidden = false;
      announce(msg);
    }
    function hush() { if (UI.msg) { UI.msg.hidden = true; UI.msg.textContent = ''; } }

    /* ---- dates ----------------------------------------------------------- */
    function renderDates() {
      if (!UI.dates) return;
      clear(UI.dates);
      var lastMon = '';
      S.dateKeys.forEach(function (key, i) {
        var list = S.byDate[key] || [];
        var d = list.length ? new Date(list[0].starts_at) : keyToDate(key);
        var b = el('button', 'hb__day');
        b.type = 'button';
        b.id = 'hb-d-' + sid + '-' + key;
        b.setAttribute('role', 'tab');
        b.setAttribute('data-key', key);
        var selected = key === S.dateKey;
        b.setAttribute('aria-selected', selected ? 'true' : 'false');
        b.tabIndex = selected ? 0 : -1;                        // roving tabindex: one stop for 14 days
        if (!list.length) b.setAttribute('aria-disabled', 'true');
        var mon = fmt(F_MON, d, '');
        var dow = fmt(F_DOW, d, '').toUpperCase();
        // Show the month on the first chip and whenever it rolls over, so a rail
        // that crosses into August is not just a run of small numbers.
        b.appendChild(el('span', 'hb__day-dow', (mon && mon !== lastMon && i > 0) ? mon.toUpperCase() : dow));
        lastMon = mon || lastMon;
        b.appendChild(el('span', 'hb__day-num', fmt(F_DAY, d, key.slice(8))));
        var mark = dayMark(key, list.length, d);
        b.appendChild(el('span', 'hb__day-n', mark.chip));
        b.setAttribute('aria-label', fullOf(d.toISOString()) + ' — ' + mark.spoken);
        b.addEventListener('click', function () { chooseDate(key, false); });
        UI.dates.appendChild(b);
      });
      if (UI.more) UI.more.hidden = !(S.maxDays > S.windowDays);
    }

    /* WHAT AN EMPTY DAY IS ALLOWED TO CLAIM.
       Every empty day used to read "Full", and most of them were not. Forged by ReFormd does not
       work Sundays at all, so a customer looking at the live rail saw SUN "Full" - which says "you
       just missed out, it was popular" about a day nobody was ever able to book. Today after the
       last slot said "Full" too, when the truth is the lead time has passed.
       "Full" is a strong claim and we can only make it with evidence, so:
         - the same weekday has openings elsewhere in the window  -> the partner DOES work that day,
           so this one being empty means it is taken. "Full" is fair.
         - that weekday is empty every time it appears           -> they do not work it. "Closed".
         - today, with nothing left                              -> "Today" is not full, it is over.
         - anything else (a one-week window, too little evidence) -> a neutral dash. Understating is
           the only harmless direction: it never tells someone a lie about why they cannot book.
       The screen reader gets the same distinction in words, not the dash. */
    function dayMark(key, count, d) {
      if (count) return { chip: String(count), spoken: count + (count === 1 ? ' time' : ' times') + ' open' };
      if (key === dkey(new Date())) return { chip: '—', spoken: 'no times left today' };
      // The weekday of the KEY, which is already the partner's local calendar day (dkey formats in
      // their timezone). Reading it off the slot Date instead would drift for a partner whose local
      // date differs from UTC's at that hour - which is most of Asia for half of every day.
      var dow = keyToDate(key).getUTCDay();
      var sameDow = 0, sameDowOpen = 0;
      S.dateKeys.forEach(function (k) {
        if (keyToDate(k).getUTCDay() !== dow) return;
        sameDow++;
        if ((S.byDate[k] || []).length) sameDowOpen++;
      });
      if (sameDowOpen > 0) return { chip: 'Full', spoken: 'fully booked' };
      if (sameDow >= 2) return { chip: 'Closed', spoken: 'not a working day' };
      return { chip: '—', spoken: 'no times open' };
    }

    // A date key back to a Date. Midday, so it lands on the right calendar day
    // in any timezone including one that shifted for DST that morning.
    function keyToDate(key) {
      var p = String(key).split('-');
      return new Date(Date.UTC(+p[0], (+p[1]) - 1, +p[2], 12, 0, 0));
    }

    function onDateKeys(e) {
      var keysNav = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 1, ArrowUp: -1 };
      var tabs = Array.prototype.slice.call(UI.dates.querySelectorAll('[role="tab"]'));
      var at = tabs.indexOf(document.activeElement);
      if (at < 0) return;
      var to = -1;
      if (Object.prototype.hasOwnProperty.call(keysNav, e.key)) to = at + keysNav[e.key];
      else if (e.key === 'Home') to = 0;
      else if (e.key === 'End') to = tabs.length - 1;
      else return;
      if (to < 0 || to >= tabs.length) { e.preventDefault(); return; }
      e.preventDefault();
      // Automatic activation: the slot data is already in memory, so moving
      // focus can select without a round trip.
      chooseDate(tabs[to].getAttribute('data-key'), true);
    }

    function chooseDate(key, focusIt) {
      if (!key || key === S.dateKey) {
        if (focusIt) focusDate(key);
        return;
      }
      S.dateKey = key;
      renderDates();
      renderTimes();
      if (focusIt) focusDate(key);
      var n = (S.byDate[key] || []).length;
      announce(fullOf(keyToDate(key).toISOString()) + ': ' + (n ? n + (n === 1 ? ' time' : ' times') + ' open' : 'no times open'));
    }
    function focusDate(key) {
      var b = UI.dates && UI.dates.querySelector('[data-key="' + key + '"]');
      if (!b) return;
      try { b.focus(); } catch (e) {}
      try { b.scrollIntoView({ block: 'nearest', inline: 'center' }); } catch (e) { /* older Safari */ }
    }

    /* ---- times ----------------------------------------------------------- */
    function renderTimes() {
      if (!UI.times) return;
      clear(UI.times);
      var list = S.dateKey ? (S.byDate[S.dateKey] || []) : [];
      var tab = S.dateKey ? ('hb-d-' + sid + '-' + S.dateKey) : '';
      if (tab && document.getElementById(tab)) UI.times.setAttribute('aria-labelledby', tab);
      else UI.times.setAttribute('aria-label', 'Available times');

      if (!list.length) {
        var empty = el('div', 'hb__empty', S.dateKeys.length
          ? 'Nothing open this day. Try another date, or tick “I’m flexible” below.'
          : 'No times loaded.');
        empty.style.gridColumn = '1 / -1';
        UI.times.appendChild(empty);
        /* THE WAITLIST, on the day they actually wanted.
           The fallback screen already offers this when the WHOLE calendar is empty, but that is the
           rare case. The common one is right here: a customer who can see plenty of availability and
           none of it on their Tuesday. Until now that person had no way to be reached either, and
           they are a better waitlist candidate than the first group - they have already told us the
           exact day. */
        if (S.dateKeys.length && S.service && S.dateKey) {
          var dayStart = new Date(S.dateKey + 'T00:00:00');
          if (!isNaN(dayStart.getTime())) {
            var wl = renderWaitlist({
              label: 'Tell me if this day opens up',
              from: dayStart.toISOString(),
              to: new Date(dayStart.getTime() + 86400000).toISOString(),
              whenLabel: fullOf(dayStart.toISOString())
            });
            wl.style.gridColumn = '1 / -1';
            UI.times.appendChild(wl);
          }
        }
        // A tabpanel with no focusable children is unreachable by keyboard, so a
        // keyboard user would never hear why the panel is empty. Make it a stop.
        UI.times.tabIndex = 0;
        return;
      }
      UI.times.removeAttribute('tabindex');

      list.forEach(function (slot) {
        var b = el('button', 'hb__slot');
        b.type = 'button';
        b.setAttribute('data-iso', slot.starts_at);
        var rank = pickIndex(slot.starts_at);
        b.setAttribute('aria-pressed', rank > -1 ? 'true' : 'false');
        var t = timeOf(slot.starts_at);
        b.appendChild(document.createTextNode(t));
        if (slot.auto_accept) b.appendChild(el('span', 'hb__slot-auto', 'Instant'));
        if (rank > -1) b.appendChild(el('span', 'hb__slot-rank', String(rank + 1)));
        b.setAttribute('aria-label', t + (slot.auto_accept ? ', instant confirmation' : '') + (rank > -1 ? ', picked as choice ' + (rank + 1) : ''));
        // seats_open is present for group classes (capacity > 1). 0 should never
        // reach us, but if it does, show it rather than sell it.
        if (typeof slot.seats_open === 'number' && slot.seats_open <= 0) {
          b.disabled = true;
          b.setAttribute('aria-label', t + ', full');
        }
        b.addEventListener('click', function () { togglePick(slot); });
        UI.times.appendChild(b);
      });
    }

    function onTimeKeys(e) {
      var step2 = { ArrowRight: 1, ArrowLeft: -1 };
      if (!Object.prototype.hasOwnProperty.call(step2, e.key)) return;
      var all = Array.prototype.slice.call(UI.times.querySelectorAll('.hb__slot'));
      var at = all.indexOf(document.activeElement);
      if (at < 0) return;
      var to = at + step2[e.key];
      if (to < 0 || to >= all.length) return;
      e.preventDefault();
      try { all[to].focus(); } catch (err) {}
    }

    /* ---- picks ----------------------------------------------------------- */
    // Instant partners take ONE time (booked on the spot); everyone else may propose up to MAX_CANDIDATES.
    function maxPicks() { return S.instant ? 1 : MAX_CANDIDATES; }
    function pickIndex(iso) {
      for (var i = 0; i < S.picks.length; i++) if (S.picks[i].starts_at === iso) return i;
      return -1;
    }

    function togglePick(slot) {
      hush();
      var at = pickIndex(slot.starts_at);
      if (at > -1) {
        S.picks.splice(at, 1);
        announce(timeOf(slot.starts_at) + (S.instant ? ' unselected.' : ' removed. ' + S.picks.length + ' of ' + maxPicks() + ' picked.'));
      } else if (S.instant) {
        // One time, booked on the spot: choosing another REPLACES it rather than stacking a list.
        S.picks = [{ starts_at: slot.starts_at, ends_at: slot.ends_at }];
        announce(timeOf(slot.starts_at) + ' selected.');
        track('booking_slot_pick', { rank: 1, partner: CFG.partner || '', service: (S.service && S.service.id) || '' });
      } else if (S.picks.length >= maxPicks()) {
        // Refuse rather than silently swap: a pick quietly disappearing is worse
        // than being told the limit.
        say(noun('Three times is the most a {noun} is asked to choose from. Remove one below to swap it out.'), 'warn');
        return;
      } else {
        S.picks.push({ starts_at: slot.starts_at, ends_at: slot.ends_at });
        announce(timeOf(slot.starts_at) + ' added as choice ' + S.picks.length + '. ' + S.picks.length + ' of ' + maxPicks() + ' picked.');
        track('booking_slot_pick', { rank: S.picks.length, partner: CFG.partner || '', service: (S.service && S.service.id) || '' });
      }
      // ADDON (a11y 2026-08-06): KEEP THE KEYBOARD WHERE IT WAS.
      // renderTimes() clears and rebuilds every .hb__slot, which destroys the
      // very button the customer just pressed — so focus fell back to <body>
      // and their next Tab restarted from the top of the document, above the
      // header, on the money path. Found by pressing Enter on a slot with a
      // real keyboard; no automated pass can see it, because the DOM afterwards
      // looks perfectly correct. The live-region announce() above was already
      // right, which is what made this easy to miss: a screen-reader user was
      // TOLD their pick registered while being silently thrown to the top.
      var keptIso = null;
      var act = document.activeElement;
      if (act && act.classList && act.classList.contains('hb__slot')) keptIso = act.getAttribute('data-iso');

      renderTimes();
      renderPicks();

      if (keptIso) {
        // Match on the slot's own identity, not its index: the list is rebuilt
        // and a rank badge can change a button's width, but the ISO is stable.
        var back = UI.times && UI.times.querySelector('.hb__slot[data-iso="' + keptIso + '"]');
        if (back) { try { back.focus(); } catch (e) {} }
      }
    }

    function renderPicks() {
      if (!UI.picks) { paintCta(); return; }   // workshop: no picks list, but the CTA still needs painting
      clear(UI.picks);
      S.picks.forEach(function (p, i) {
        var li = el('li', 'hb__pick');
        if (!S.instant) li.appendChild(el('span', 'hb__pick-rank', ordinal(i) + ' choice'));
        li.appendChild(el('span', null, shortWhen(p.starts_at)));
        var x = el('button', 'hb__pick-x', '×');
        x.type = 'button';
        x.setAttribute('aria-label', 'Remove ' + whenOf(p.starts_at));
        x.addEventListener('click', function () { togglePick(p); });
        li.appendChild(x);
        UI.picks.appendChild(li);
      });
      if (UI.count) UI.count.textContent = S.picks.length + ' of ' + maxPicks();
      paintOdds();
      paintCta();
    }
    function ordinal(i) { return ['1st', '2nd', '3rd'][i] || (i + 1) + 'th'; }
    function shortWhen(iso) {
      var d = new Date(iso);
      return fmt(F_DOW, d, '') + ' ' + fmt(F_DAY, d, '') + ' ' + fmt(F_MON, d, '') + ', ' + timeOf(iso);
    }

    // Encouragement, not a validation error: one pick is a valid request, two or
    // three just give the partner something to say yes to.
    function paintOdds() {
      if (!UI.odds) return;
      // Instant partners take one time; there is no "add a second so they can choose" to encourage.
      if (S.instant) { UI.odds.hidden = true; UI.odds.textContent = ''; return; }
      var n = S.picks.length;
      var copy = '';
      if (n === 1) copy = noun('One time works — add a second and your {noun} can pick whichever fits their day.');
      else if (n === 2) copy = 'Two options. Add a third and you’ve given them a real choice.';
      else if (n === 3) copy = noun('Three options, ranked. Your {noun} takes the first one that works.');
      UI.odds.textContent = copy;
      UI.odds.hidden = !copy;
    }

    function paintCta() {
      if (!UI.go) return;
      var n = S.picks.length;
      // WORKSHOP: no time-picking. The button books the auto-held earliest slot; email + WhatsApp are
      // validated at submit like every other partner. Enabled once any waiver/screening is satisfied,
      // disabled only when there is genuinely no availability to hold (n === 0).
      if (S.workshop) {
        UI.go.textContent = CFG.ctaWorkshop || 'Book & pay';
        UI.go.disabled = n === 0 || !waiverOk() || !screeningOk();
        return;
      }
      var label;
      if (S.instant) label = n === 0 ? 'Pick a time' : (CFG.ctaInstant || 'Book instantly');
      else if (n === 0) label = (UI.flex && UI.flex.checked) ? 'Send a flexible request' : 'Pick a time';
      else if (n === 1) label = CFG.cta || 'Request this time';
      else label = (CFG.cta || 'Request these times') + ' (' + n + ')';
      UI.go.textContent = label;
      // A missing acknowledgement disables the button rather than failing at submit: the customer can
      // see WHY, because the unticked box is right there. Same for the screening confirm.
      UI.go.disabled = (n === 0 && !(UI.flex && UI.flex.checked)) || !waiverOk() || !screeningOk();
    }

    // Every waiver box ticked (or none configured).
    function waiverOk() {
      if (!UI.waiver || !UI.waiver.length) return true;
      for (var i = 0; i < UI.waiver.length; i++) if (!UI.waiver[i].box.checked) return false;
      return true;
    }

    // TWO conditions gate a screened booking, and both are required: the customer must have OPENED the
    // partner's form (S.screeningOpened) AND ticked the confirm. A service with no gate (no UI.screenBox)
    // is always ok, so a normal booking is untouched.
    function screeningOk() {
      return !UI.screenBox || (S.screeningOpened && UI.screenBox.checked);
    }

    // Stash the contact fields the customer has typed into CFG so a form repaint (the intake_required
    // recovery) restores them instead of blanking. Picks live on S and survive a repaint on their own.
    function preserveInputs() {
      try {
        if (UI.email) CFG.customerEmail = String(UI.email.value || '').trim() || CFG.customerEmail;
        if (UI.name) CFG.customerName = String(UI.name.value || '').trim() || CFG.customerName;
        if (UI.phone) CFG.customerPhone = String(UI.phone.value || '').trim() || CFG.customerPhone;
      } catch (e) { /* preserving inputs is a nicety, never load-bearing */ }
    }

    function noteValue() {
      if (!UI.flex || !UI.flex.checked) return '';
      return String((UI.note && UI.note.value) || '').trim();
    }

    /* =======================================================================
       LOAD
       ===================================================================== */
    function skeleton() {
      clear(body);
      var wrap = el('div', 'hb__skel');
      wrap.setAttribute('aria-hidden', 'true');
      for (var r = 0; r < 3; r++) {
        var row = el('div', 'hb__skel-row');
        for (var c = 0; c < (r === 0 ? 5 : 4); c++) row.appendChild(el('div', 'hb__skel-b'));
        wrap.appendChild(row);
      }
      body.appendChild(wrap);
      var p = el('p', 'hb__hint', 'Loading open times…');
      p.setAttribute('role', 'status');
      body.appendChild(p);
    }

    function load() {
      if (!BASE || !CFG.partner) { setMode('fallback', 'unconfigured'); return; }
      skeleton();
      api('services', { query: { partner: CFG.partner } })
        .then(function (data) {
          var list = (Array.isArray(data) ? data : (data && data.services) || []).filter(function (s) {
            return s && s.id && (s.status == null || s.status === 'active');
          });
          if (CFG.serviceId) list = list.filter(function (s) { return s.id === CFG.serviceId; });
          if (!list.length) throw apiErr('not_found', 'no services for partner');
          S.services = list;
          // Instant partners (Wix/native, auto-confirmed) book the first pick on the spot; the services
          // response carries partner.instant, and it decides single-pick vs propose-up-to-three below.
          S.instant = !!(data && data.partner && data.partner.instant);
          // WORKSHOP (BikeCraft One): a whatsapp-handoff partner. The slot is a PLACEHOLDER settled in
          // chat, so this widget skips time-picking entirely and holds the earliest open slot — see the
          // render branch, group() auto-pick, and paintCta below. Exclusive to whatsapp-handoff
          // partners (the backend sets partner.workshop off handoff_mode === 'whatsapp').
          S.workshop = !!(data && data.partner && data.partner.workshop);
          /* WHICH SESSION IS SELECTED WHEN THE PAGE OPENS.
             This template is shared by every bookable product of every partner, so the widget lists
             the whole partner catalogue. Defaulting to list[0] meant defaulting to whatever the API
             returned first - and it orders by price ASCENDING. On the VO2max page ($228) that
             preselected the Resting Metabolic Rate Test ($148): a customer who picked a time and
             checked out bought a different session, at a different price, from the one whose page
             they were reading. Match the product's OWN variant first (CFG.variantId is the product's
             selected/first variant, rendered by the section on a product template) and fall back to
             the old behaviour everywhere else - a page with no product, or a service row whose
             shopify_variant_id has not been seeded, behaves exactly as before. */
          // PINNED = this widget is on ONE test's own page (its product variant matched a service, or
          // a service id was set). On a pinned page the customer came for THIS test, so the full
          // "choose your session" list of the partner's whole catalogue is noise - we show just this
          // test (and its machine selector). A generic partner page (no product match) still lists
          // everything. The machine selector is unaffected either way.
          var matched = pickForProduct(list);
          S.service = matched || list[0];
          S.pinned = !!matched || !!CFG.serviceId;
          S.maxDays = Math.max.apply(null, list.map(function (s) { return parseInt(s.horizon_days, 10) || 30; }));
          return fetchAvailability();
        })
        .then(function () {
          if (!S.slots.length) return setMode('fallback', 'empty');
          setMode('form');
          track('booking_view', { partner: CFG.partner || '', service: (S.service && S.service.id) || '', slots: S.slots.length });
        })
        .catch(function (e) {
          if (e && (e.code === 'partner_paused' || e.code === 'not_found')) return setMode('fallback', e.code);
          setMode('fallback', (e && e.code) || 'offline');
        });
    }

    /* The service whose OWN Shopify variant is the product being viewed, or null.
       Deliberately does NOT go through variantFor(): that falls back to CFG.variantId, so a service
       row with no variant id of its own would "match" the page and we would preselect by accident
       exactly the row that is mis-seeded. Only a real, equal, digits-only id counts as a match. */
    function pickForProduct(list) {
      /* pageVariantId is the variant of the product whose page this is; variantId is the
         customizer's mis-seeded-row override and is only a sensible second guess. Reading only
         variantId is what made the first version of this fix INERT on every real product page -
         that setting is blank by default, so nothing ever matched and list[0] still won. */
      var want = String(CFG.pageVariantId || CFG.variantId || '').replace(/[^0-9]/g, '');
      if (!want) return null;
      for (var i = 0; i < list.length; i++) {
        var own = String((list[i] && (list[i].shopify_variant_id || list[i].variant_id)) || '').replace(/[^0-9]/g, '');
        if (own && own === want) return list[i];
      }
      return null;
    }

    /* MODALITY GROUPS — the same test on different machines (Forged's VO2max on treadmill / Wahoo bike
       / Wattbike, Lactate on treadmill / row / ski / bike, FTP on Wahoo / Wattbike). The backend tags
       each row with modality_group (the BASE service's id, shared by every machine in the group) and a
       modality_label ("Treadmill", "Bike (Wahoo)"…). A service outside any group has neither, so all of
       this is inert until a group exists. Each machine is its own service + Shopify variant, which is
       what lets it be booked and paid for correctly (the paid path refuses a cart line whose variant is
       not the booked service's own). */
    // The row that REPRESENTS a group in the top-level "choose your session" list: an ungrouped service,
    // or the group's base (whose modality_group equals its own id). Machine variants are not listed at
    // the top level - they are reached through the machine selector once their test is chosen.
    function isPrimaryService(svc) {
      return !!svc && (!svc.modality_group || String(svc.modality_group) === String(svc.id));
    }
    // Every machine in the same group as `svc`, base included, in a stable order (base first, then by
    // label). One member (or none) means there is no machine choice to offer.
    function modalitySiblings(svc) {
      if (!svc || !svc.modality_group) return [];
      var g = String(svc.modality_group);
      var out = S.services.filter(function (s) { return s.modality_group && String(s.modality_group) === g; });
      return out.sort(function (a, b) {
        var ab = isPrimaryService(a) ? 0 : 1, bb = isPrimaryService(b) ? 0 : 1;
        if (ab !== bb) return ab - bb;
        return String(a.modality_label || '').localeCompare(String(b.modality_label || ''));
      });
    }

    function chooseService(svc) {
      if (!svc || (S.service && svc.id === S.service.id)) return;
      // A different TEST is a different form, so the screening must be opened again. Switching MACHINE
      // within the same test keeps it (same form), so a cyclist toggling Wahoo vs Wattbike is not made
      // to re-open what they already did.
      var groupKey = function (s) { return s ? String(s.modality_group || s.id) : ''; };
      if (groupKey(svc) !== groupKey(S.service)) S.screeningOpened = false;
      S.service = svc;
      S.picks = [];
      S.dateKey = null;
      skeleton();
      fetchAvailability()
        .then(function () {
          setMode('form');
          announce((svc.title || 'Session') + ' selected. ' + S.slots.length + ' times open.');
        })
        .catch(function (e) { setMode('fallback', (e && e.code) || 'offline'); });
    }

    function fetchAvailability() {
      if (!S.service) return Promise.reject(apiErr('not_found'));
      var now = Date.now();
      S.lastFetch = now;
      var from = new Date(now);
      var to = new Date(now + S.windowDays * 86400000);
      var avail = api('availability', {
        query: { service: S.service.id, from: from.toISOString(), to: to.toISOString() }
      }).then(function (data) {
        var slots = Array.isArray(data) ? data : (data && data.slots) || [];
        // Trust nothing: drop malformed rows and anything already in the past
        // (a slow response can arrive after its own first slot has gone).
        var cut = Date.now();
        S.slots = slots.filter(function (s) {
          return s && s.starts_at && !isNaN(new Date(s.starts_at).getTime()) && new Date(s.starts_at).getTime() > cut;
        }).sort(function (a, b) { return new Date(a.starts_at) - new Date(b.starts_at); });
        group();
      });
      // Read the intake-form gate ALONGSIDE availability so the screening step is decided before the
      // form paints. fetchIntake() never rejects, so it cannot fail a browse: the only thing riding on
      // it is whether we show the screening prompt up front. The hold's own 428 remains the real gate.
      return Promise.all([avail, fetchIntake()]).then(function () {});
    }

    // Does THIS service require the partner's own screening form before booking? Sets S.intake to
    // { required, url }. Never rejects - a failed read must NEVER block a browse or a hold; if the
    // service really is gated and we missed it, the hold returns intake_required and onError() routes
    // the customer to the form then. `required` is only true when we also have a URL to send them to.
    function fetchIntake() {
      S.intake = null;
      if (!S.service) return Promise.resolve();
      var id = S.service.id;
      return api('intake-link', { method: 'POST', body: { service_id: id } })
        .then(function (d) {
          // Guard against a stale response after the customer switched services mid-flight.
          if (!S.service || S.service.id !== id) return;
          S.intake = (d && d.required && d.url) ? { required: true, url: String(d.url) } : { required: false, url: '' };
        })
        .catch(function () { S.intake = { required: false, url: '' }; });
    }

    function group() {
      S.byDate = {};
      S.slots.forEach(function (s) {
        var k = dkey(new Date(s.starts_at));
        (S.byDate[k] = S.byDate[k] || []).push(s);
      });
      // Build the rail across the WHOLE window, not just days that have slots,
      // so a fully-booked stretch shows as "Full" instead of vanishing.
      // 6-hour steps + dedupe: never skips or repeats a local date, even across
      // a DST boundary in a partner timezone that has one.
      S.dateKeys = [];
      var seen = {};
      var start = Date.now();
      var end = start + S.windowDays * 86400000;
      // Stop exactly AT `end`, never past it: a day beyond the window we asked
      // the backend for has no slots for the honest reason that we never asked
      // about it, and rendering it as "Full" would be a lie.
      for (var t = start; t <= end; t += 21600000) {
        var k = dkey(new Date(t));
        if (!seen[k]) { seen[k] = 1; S.dateKeys.push(k); }
      }
      // Land on the first day that actually has something bookable.
      if (!S.dateKey || !S.byDate[S.dateKey]) {
        S.dateKey = null;
        for (var i = 0; i < S.dateKeys.length; i++) {
          if ((S.byDate[S.dateKeys[i]] || []).length) { S.dateKey = S.dateKeys[i]; break; }
        }
        if (!S.dateKey) S.dateKey = S.dateKeys[0] || null;
      }
      // Drop any pick that no longer exists in fresh availability - that is the
      // whole point of re-rendering after slot_taken.
      var live = {};
      S.slots.forEach(function (s) { live[s.starts_at] = 1; });
      S.picks = S.picks.filter(function (p) { return live[p.starts_at]; });
      // WORKSHOP: the customer never picks — always hold the EARLIEST open slot as the placeholder
      // (real drop-off/pickup is agreed on WhatsApp; the partner can Move it after). Recomputed on
      // every availability read, so a background refresh keeps the placeholder pointing at a live slot.
      if (S.workshop) S.picks = S.slots.length ? [{ starts_at: S.slots[0].starts_at, ends_at: S.slots[0].ends_at }] : [];
    }

    // Never ask for more than the service's own horizon_days - the backend will
    // clamp it anyway and a longer window is a bigger query for nothing.
    function extend() {
      S.windowDays = Math.min(S.maxDays || 30, S.windowDays + WINDOW_DAYS);
      if (UI.more) { UI.more.disabled = true; UI.more.textContent = 'Loading…'; }
      fetchAvailability()
        .then(function () {
          setMode('form');
          announce('Showing the next ' + S.windowDays + ' days.');
        })
        .catch(function (e) { setMode('fallback', (e && e.code) || 'offline'); });
    }

    // Re-read availability and repaint. Used after slot_taken (a normal race) and
    // after a hold expires.
    //
    // The message is shown IMMEDIATELY and the slot repaint happens in place a
    // moment later. Re-rendering the whole form first would wipe the very message
    // that explains why the times just changed, and a silent shuffle of the
    // calendar is exactly how a normal race starts feeling like a bug.
    // Coalesced against lastFetch so a burst of races cannot hammer the function.
    function refresh(msg, kind) {
      if (!S.service) { load(); return; }         // restored-hold path: services were never fetched
      setMode('form');
      if (msg) say(msg, kind || 'warn');
      var wait = Math.max(0, REFETCH_GAP_MS - (Date.now() - S.lastFetch));
      setTimeout(function () {
        fetchAvailability()
          .then(function () {
            if (!S.slots.length) return setMode('fallback', 'empty');
            renderDates(); renderTimes(); renderPicks();     // in place: keeps the message on screen
          })
          .catch(function (e) { setMode('fallback', (e && e.code) || 'offline'); });
      }, wait);
    }

    // Fingerprint of what is on offer right now, so a background re-read that
    // changed nothing costs zero DOM churn (and never steals focus).
    function slotSig() { return S.slots.map(function (s) { return s.starts_at; }).join(','); }

    // PROACTIVE background revalidation. Called by the timer and by the shared
    // focus/visibility listener. It must be gentle and, above all, must never
    // disrupt a customer who is mid-form:
    //   * only ever runs on the 'form' screen, with a service chosen, tab visible,
    //     and no submit in flight;
    //   * coalesced against the last fetch, same as refresh();
    //   * a background network hiccup is swallowed silently - the submit-time
    //     LIVE check is the real guard, so a failed hint must not throw the
    //     customer to the fallback screen or wipe what they have typed;
    //   * if one of their CHOSEN times has since filled, it repaints and says so
    //     (that is the whole point - tell them before they finish the form);
    //   * otherwise it repaints only when the offer actually changed AND the
    //     customer is not currently focused inside the date/time picker, so a
    //     periodic tick never yanks a slot out from under an active click.
    function maybeRevalidate(reason) {
      if (S.mode !== 'form' || !S.service || S.busy || document.hidden) return;
      if (Date.now() - S.lastFetch < REFETCH_GAP_MS) return;         // just fetched — let it be
      var picksBefore = S.picks.map(function (p) { return p.starts_at; });
      var sigBefore = slotSig();
      fetchAvailability().then(function () {
        if (S.mode !== 'form' || S.busy) return;                    // customer advanced during the fetch
        var lost = picksBefore.filter(function (k) { return !S.picks.some(function (p) { return p.starts_at === k; }); });
        var changed = slotSig() !== sigBefore;
        if (!lost.length && !changed) return;                       // nothing moved
        if (!lost.length) {
          // No chosen time was lost. Repaint the fresher view, but not if it would
          // yank focus out of an active pick — defer that to the next focus event.
          var ae = document.activeElement;
          var picking = ae && ((UI.dates && UI.dates.contains(ae)) || (UI.times && UI.times.contains(ae)));
          if (picking) return;
        }
        renderDates(); renderTimes(); renderPicks();
        if (lost.length) say('One of your chosen times just filled — here are the latest open times.', 'warn');
        track('booking_revalidate', { reason: reason || '', lost: lost.length, changed: changed ? 1 : 0 });
      }).catch(function () { /* a background hint may fail quietly; the hold's live check still protects money */ });
    }

    /* =======================================================================
       SUBMIT: hold -> add to cart (with the line item properties)
       ===================================================================== */
    function submit() {
      if (S.busy) return;
      hush();
      if (!S.service) return say('Choose a session first.', 'warn');
      // Belt and braces: the button is already disabled, but a disabled button is a UI state, not a
      // guarantee - anything that calls submit() another way still has to pass this.
      if (!waiverOk()) return say('Please confirm all three statements above before we hold your slot.', 'warn');
      if (!screeningOk()) return say(noun('Please confirm you’ve completed the screening form before we hold your slot.'), 'warn');

      var note = noteValue();
      if (!S.picks.length) {
        // Flexible with no concrete time cannot become a hold (a hold needs a
        // startsAt) and therefore cannot reach checkout. Route it to the human
        // path with the note carried over, rather than fail silently.
        if (UI.flex && UI.flex.checked) {
          track('booking_flexible_request', { partner: CFG.partner || '' });
          return setMode('fallback', 'flexible');
        }
        return say('Pick at least one time from the calendar above.', 'warn');
      }

      var email = String(UI.email.value || '').trim();
      if (!validEmail(email)) {
        UI.email.wrap.className = 'hb__field is-bad';
        UI.email.input.setAttribute('aria-invalid', 'true');
        try { UI.email.input.focus(); } catch (e) {}
        return say('We need a working email — it’s where your confirmation, calendar invite and (if it comes to it) refund notice go.', 'warn');
      }
      UI.email.wrap.className = 'hb__field';
      UI.email.input.removeAttribute('aria-invalid');

      // Checked BEFORE the hold, in the same place and the same way as the email, because a hold is
      // a slot taken off a real calendar - refusing here costs nothing and refusing one step later
      // costs somebody else's 10am. `input.required` alone would not do it: this button is a plain
      // <button type="button">, so no native form validation ever runs.
      var phone = String(UI.phone.value || '').trim();
      if (PH_REQ && !validPhone(phone)) {
        UI.phone.wrap.className = 'hb__field hb__field--wide is-bad';
        UI.phone.input.setAttribute('aria-invalid', 'true');
        try { UI.phone.input.focus(); } catch (e) {}
        return say(WA_REQ
          ? noun('We need a WhatsApp number — it is how the {noun} confirms your time with you.')
          : noun('We need a mobile number — your {noun} uses it to reach you about this booking.'), 'warn');
      }
      UI.phone.wrap.className = 'hb__field hb__field--wide';
      UI.phone.input.removeAttribute('aria-invalid');

      var variant = variantFor(S.service);
      if (!variant) {
        // No variant = no way to take payment. Do NOT create a hold we cannot convert.
        say(noun(ERR_COPY.internal), 'bad');
        track('booking_no_variant', { service: S.service.id });
        return;
      }

      busy(true, 'Holding your time…');
      var primary = S.picks[0];
      api('hold', {
        method: 'POST',
        body: {
          serviceId: S.service.id,
          startsAt: primary.starts_at,
          customerEmail: email,
          customerName: String(UI.name.value || '').trim() || undefined,
          customerPhone: phone || undefined,
          // Only the 1st choice is held as occupancy - holding all three would
          // block a partner's whole evening for one browser. 2nd/3rd ride along
          // as the request's candidates.
          candidates: S.picks.map(function (p) { return { starts_at: p.starts_at, ends_at: p.ends_at }; }),
          flexibleNote: note || undefined
        }
      }).then(function (hold) {
        if (!hold || !hold.holdId || !hold.holdToken) throw apiErr('internal', 'hold response missing id/token');
        track('booking_hold', { partner: CFG.partner || '', service: S.service.id, candidates: S.picks.length, hold_id: hold.holdId });
        return addToCart(hold, variant, email, note, phone);
      }).catch(function (e) {
        busy(false);
        onError(e);
      });
    }

    function variantFor(svc) {
      var v = svc && (svc.shopify_variant_id || svc.variant_id);
      // Theme-side override exists so a mis-seeded variant id can be fixed from
      // the customizer without a backend deploy.
      return String(v || CFG.variantId || '').replace(/[^0-9]/g, '') || null;
    }

    function addToCart(hold, variant, email, note, phone) {
      var props = {};
      props[LI.HOLD_ID] = hold.holdId;
      // NOTE: /cart.js exposes line item properties to this browser, so the hold
      // token is visible to the customer who owns the hold. That is acceptable
      // only because the token does one thing - convert THIS hold on THIS order.
      // It must never be usable to read or cancel anything else.
      props[LI.HOLD_TOKEN] = hold.holdToken;
      props[LI.STARTS_AT] = hold.startsAt || S.picks[0].starts_at;    // server's echo wins
      props[LI.SERVICE_ID] = S.service.id;
      // Omit rather than send empty: an empty property still rides to the order
      // and reads as "partner unknown" instead of "not supplied by the widget".
      var pid = S.service.partner_id || CFG.partnerId || '';
      if (pid) props[LI.PARTNER_ID] = pid;
      // The hold contract does not promise a request id. Set it only if the
      // backend returned one; an empty property is worse than an absent one.
      var rid = hold.requestId || hold.request_id || null;
      if (rid) props[LI.REQUEST_ID] = rid;
      var alts = S.picks.slice(1);
      if (alts.length) props[LI_X.CANDIDATES] = JSON.stringify(alts);
      if (note) props[LI_X.FLEXIBLE] = note;
      // Sent whenever the customer typed one, not only when WA_REQ is on: a number volunteered into
      // the optional box is just as useful to a partner, and there is no reason to throw it away.
      // Omitted rather than sent empty, like every other optional property here - jobs.ts falls back
      // to the order's own phone, and an empty string would beat that fallback to nothing.
      if (validPhone(phone)) props[LI_X.WHATSAPP] = String(phone).trim();

      /* The waiver, as VISIBLE properties (no underscore prefix), which is what makes them show on
         the order, the packing slip and the customer's confirmation - exactly as they did when the
         product-options app collected them. Storing the acknowledged TEXT rather than a bare "yes"
         is the point: what a customer agreed to has to be readable a year later, when the wording on
         the page may have moved on. */
      (UI.waiver || []).forEach(function (w) {
        if (w.box && w.box.checked && w.text) props[w.name] = w.text;
      });

      // Same abort/timeout as api(): this is the LAST step and it runs AFTER a real hold exists, so a
      // stalled /cart/add (a mobile drop mid-flight never rejects on its own) would otherwise hang the
      // button on "Holding your time…" forever, with the slot blocked for the whole TTL and S.hold never
      // stored so a reload cannot release it. On timeout we abort — landing in the catch below with
      // S.hold still unset, which releases the hold.
      var cartCtl = null, cartTimer = null;
      var cartInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant, quantity: 1, properties: props }] })
      };
      try { cartCtl = new AbortController(); cartInit.signal = cartCtl.signal; } catch (e) { cartCtl = null; }
      if (cartCtl) cartTimer = setTimeout(function () { try { cartCtl.abort(); } catch (e) {} }, FETCH_MS);
      return fetch('/cart/add.js', cartInit).then(function (r) {
        if (cartTimer) clearTimeout(cartTimer);
        return r.json().catch(function () { return null; });
      }).then(function (res) {
        if (!res || (res.status && res.status >= 400) || res.description) {
          throw apiErr('cart', (res && (res.description || res.message)) || 'cart rejected the line');
        }
        var item = (res.items && res.items[0]) || res;
        S.hold = {
          holdId: hold.holdId, holdToken: hold.holdToken,
          expiresAt: hold.expiresAt, startsAt: props[LI.STARTS_AT], endsAt: hold.endsAt || S.picks[0].ends_at,
          requestId: rid || '', serviceId: S.service.id, title: S.service.title || '',
          partnerId: CFG.partner || '', lineKey: (item && item.key) || ''
        };
        store(S.hold);
        // From here the line EXISTS. Nothing below may throw into the catch
        // below it, or a purely cosmetic error would release a hold that is
        // sitting in a real cart line.
        try {
          busy(false);
          setMode('hold');
          announce(S.workshop
            ? 'Booked and held in your bag — check out to send it. The workshop confirms your drop-off on WhatsApp.'
            : 'Time held. ' + whenOf(S.hold.startsAt) + ' is in your bag — check out to send the request.');
          if (window.__hubAnnounce) window.__hubAnnounce('Session added to your bag');
          track('booking_add_to_cart', {
            partner: CFG.partner || '', service: S.service.id, hold_id: hold.holdId,
            request_id: rid || '', value: (S.service.price_cents || 0) / 100, currency: CFG.currency || 'SGD'
          });
        } catch (uiErr) { /* the booking is safe; only the repaint failed */ }
      }).catch(function (e) {
        if (cartTimer) clearTimeout(cartTimer);
        if (S.hold) return;                    // the line was created - not a failure path
        // The hold is real but there is no cart line to convert it. Release it
        // now or the slot sits blocked for the full TTL for nothing.
        releaseRaw(hold.holdId, hold.holdToken);
        busy(false);
        // A cart rejection and a timed-out/aborted add end the same way — hold released, nothing
        // charged — so both get the recoverable message rather than a raw error.
        if (e && (e.code === 'cart' || e.name === 'AbortError')) {
          say('We held your time but couldn’t add the session to your bag, so we let the time go. Reload and try again — nothing was charged.', 'bad');
          track('booking_cart_fail', { service: S.service.id, detail: String(e.message || 'timeout').slice(0, 120) });
          return;
        }
        onError(e);
      });
    }

    function busy(on, label) {
      S.busy = !!on;
      if (!UI.go) return;
      UI.go.disabled = !!on;
      if (on) { UI.go.setAttribute('data-label', UI.go.textContent); UI.go.textContent = label || 'Working…'; }
      else { var old = UI.go.getAttribute('data-label'); if (old) UI.go.textContent = old; paintCta(); }
    }

    /* =======================================================================
       HOLD PANEL + COUNTDOWN
       ===================================================================== */
    function renderHold() {
      stopClock();
      var h = S.hold;
      if (!h) { setMode('form'); return; }
      var wrap = el('div', 'hb__hold');
      wrap.hidden = false;
      // WORKSHOP: the held slot is an invisible placeholder (settled on WhatsApp), so NEVER show a
      // date/time here - it would read as a scheduled appointment the customer never chose. Show the
      // service only, and reword the countdown to what it actually is: the cart reservation TTL.
      var isWk = S.workshop;
      wrap.appendChild(el('p', 'hb__hold-h', isWk ? 'Reserved for you' : 'Time held for you'));
      wrap.appendChild(el('p', 'hb__hold-when', isWk
        ? (h.title || 'Your service')
        : (whenOf(h.startsAt) + (h.title ? ' · ' + h.title : ''))));

      var sub = el('p', 'hb__hold-sub');
      sub.appendChild(document.createTextNode(isWk ? 'Held in your bag for ' : 'This slot is yours for '));
      UI.clock = el('span', 'hb__clock', '--:--');
      // The ticker must NOT be a live region: announcing every second would make
      // the page unusable with a screen reader. Milestones are announced instead.
      UI.clock.setAttribute('aria-hidden', 'true');
      sub.appendChild(UI.clock);
      // ...but hiding the ticker left the SENTENCE with a hole in it: a screen reader
      // read "This slot is yours for ." and the one number that matters was gone.
      // This span carries a COARSE duration ("about 10 minutes") in the same place.
      // It is off-screen, not a live region, and only repaints when the rounded
      // minute changes - so reading the paragraph gives a real answer while nothing
      // is ever announced per second.
      UI.clockA11y = el('span', 'hb__sr');
      sub.appendChild(UI.clockA11y);
      sub.appendChild(document.createTextNode(isWk
        ? '. Check out to send it — the workshop confirms your drop-off or pickup on WhatsApp.'
        : noun('. Check out to send the request — your {noun} then confirms.')));
      wrap.appendChild(sub);

      var actions = el('div', 'hb__hold-actions');
      var go = el('a', 'hb__btn hb__btn--go', 'Check out');
      go.href = '/checkout';
      go.addEventListener('click', function () { track('booking_checkout_click', { hold_id: h.holdId }); });
      actions.appendChild(go);
      var bag = el('a', 'hb__btn', 'View bag');
      bag.href = '/cart';
      actions.appendChild(bag);
      var rel = el('button', 'hb__btn', 'Release this time');
      rel.type = 'button';
      rel.addEventListener('click', function () { rel.disabled = true; releaseAndReset('released'); });
      actions.appendChild(rel);
      wrap.appendChild(actions);

      UI.msg = el('div', 'hb__msg');
      UI.msg.hidden = true;

      body.appendChild(wrap);
      body.appendChild(UI.msg);
      if (CFG.fine) body.appendChild(el('p', 'hb__fine', CFG.fine));
      startClock();
    }

    function startClock() {
      stopClock();
      S.warned = {};
      var paint = function () {
        var h = S.hold;
        if (!h || !UI.clock) return stopClock();
        var left = Math.floor((new Date(h.expiresAt).getTime() - Date.now()) / 1000);
        if (isNaN(left)) { UI.clock.textContent = 'a few minutes'; setSpoken('a few minutes'); return; }
        if (left <= 0) { stopClock(); return onHoldExpired(); }
        var m = Math.floor(left / 60), s = left % 60;
        UI.clock.textContent = m + ':' + (s < 10 ? '0' : '') + s;
        UI.clock.classList.toggle('is-urgent', left <= 120);
        // Coarse on purpose. "9 minutes 41 seconds" is worse to hear than "about 10
        // minutes", and rounding up means the spoken number is never optimistic.
        setSpoken(left <= 60 ? 'less than a minute'
          : 'about ' + Math.ceil(left / 60) + (Math.ceil(left / 60) === 1 ? ' minute' : ' minutes'));
        if (left <= 300 && !S.warned.m5) { S.warned.m5 = 1; announce('Five minutes left on your held time.'); }
        if (left <= 60 && !S.warned.m1) { S.warned.m1 = 1; announce('One minute left on your held time.'); }
      };
      paint();
      S.tick = setInterval(paint, 1000);
    }
    function stopClock() { if (S.tick) { clearInterval(S.tick); S.tick = null; } }

    // Only writes when the wording actually changes. A screen reader that happens to be
    // sitting on this paragraph re-reads it when the text node is replaced, so rewriting
    // an identical string once a second would reintroduce exactly the chatter the
    // aria-hidden ticker was added to prevent.
    function setSpoken(text) {
      if (!UI.clockA11y || UI.clockA11y.textContent === text) return;
      UI.clockA11y.textContent = text;
    }

    // Expiry is not an error the customer caused. Pull the dead line out of the
    // cart (checkout against an expired hold would fail at orders/paid), then
    // show fresh availability.
    function onHoldExpired() {
      var h = S.hold;
      track('booking_hold_expired', { hold_id: (h && h.holdId) || '' });
      dropCartLine(h && h.lineKey);
      S.hold = null;
      store(null);
      refresh(noun(ERR_COPY.hold_expired), 'warn');   // refresh() renders the form itself
    }

    function releaseAndReset(reason) {
      var h = S.hold;
      if (!h) { setMode('form'); return; }
      stopClock();
      track('booking_release', { hold_id: h.holdId, reason: reason || 'manual' });
      releaseRaw(h.holdId, h.holdToken);
      dropCartLine(h.lineKey);
      S.hold = null;
      store(null);
      refresh('Time released — nothing was charged. Pick again whenever you like.', 'ok');
    }

    // Fire-and-forget: if the release call fails, cron-expire-holds still frees
    // the slot at TTL, so there is nothing useful to tell the customer.
    function releaseRaw(holdId, holdToken) {
      if (!holdId || !holdToken) return;
      api('release', { method: 'POST', body: { holdId: holdId, holdToken: holdToken } }).catch(function () {});
    }

    function dropCartLine(key) {
      if (!key) return Promise.resolve();
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      }).catch(function () {});
    }

    /* =======================================================================
       ERROR ROUTING
       ===================================================================== */
    function onError(e) {
      var code = (e && e.code) || 'internal';
      if (code === 'slot_taken') {
        // The normal race. Drop the pick that lost, keep the rest, and repaint
        // fresh availability immediately - this is not a dead end.
        var lost = S.picks.length ? S.picks[0] : null;
        if (lost) S.picks = S.picks.filter(function (p) { return p.starts_at !== lost.starts_at; });
        track('booking_slot_taken', { partner: CFG.partner || '', service: (S.service && S.service.id) || '' });
        refresh(noun(ERR_COPY.slot_taken), 'warn');
        return;
      }
      if (code === 'hold_expired') { onHoldExpired(); return; }
      if (code === 'intake_required') {
        // The hold's own 428 gate: the partner's form is not completed for this email yet. NOT a dead
        // end. Force the screening OPEN + confirm again, preserve what they typed, and repaint so the
        // card is back to its locked first-step state at the top.
        track('booking_intake_required', { partner: CFG.partner || '', service: (S.service && S.service.id) || '' });
        S.screeningOpened = false;
        preserveInputs();
        if (S.intake && S.intake.required && S.intake.url) {
          setMode('form');                           // rebuilds the locked screening card (picks survive on S)
          say(noun(ERR_COPY.intake_required), 'warn');
          return;
        }
        // The gate read failed at load, so no card was shown. Fetch the link, then repaint.
        fetchIntake().then(function () {
          if (S.intake && S.intake.required && S.intake.url) {
            setMode('form');
            say(noun(ERR_COPY.intake_required), 'warn');
          } else {
            // Cannot reach the form path at all - hand to a human rather than loop the customer.
            setMode('fallback', 'upstream_failed');
          }
        });
        return;
      }
      if (code === 'outside_window' || code === 'bad_input') { refresh(noun(ERR_COPY[code]), 'warn'); return; }
      if (code === 'partner_paused' || code === 'not_found') { setMode('fallback', code); return; }
      if (code === 'offline' || code === 'upstream_failed') { setMode('fallback', code); return; }
      say(noun(ERR_COPY[code] || ERR_COPY.internal), 'bad');
      track('booking_error', { code: code });
    }

    /* =======================================================================
       RESTORE a hold across a reload, and detect a customer backing out by
       emptying their bag in the cart drawer.
       ===================================================================== */
    function restore() {
      var h = stored();
      if (!h || !h.holdId || !h.holdToken) return false;
      if (CFG.serviceId && h.serviceId && h.serviceId !== CFG.serviceId) return false;   // another widget's hold
      if (CFG.partner && h.partnerId && h.partnerId !== CFG.partner) return false;       // another partner's hold (generic coaching page: serviceId is blank, so the check above can't catch it)
      if (new Date(h.expiresAt).getTime() <= Date.now()) {
        releaseRaw(h.holdId, h.holdToken);
        dropCartLine(h.lineKey);
        store(null);
        return false;
      }
      S.hold = h;
      setMode('hold');
      // If the line is gone from the cart, the customer removed it - that IS
      // backing out, so free the slot instead of sitting on it until TTL.
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var items = (cart && cart.items) || [];
          var still = items.some(function (it) {
            var p = it.properties || {};
            return (h.lineKey && it.key === h.lineKey) || p[LI.HOLD_ID] === h.holdId;
          });
          if (!still) {
            track('booking_release', { hold_id: h.holdId, reason: 'cart_emptied' });
            releaseRaw(h.holdId, h.holdToken);
            S.hold = null;
            store(null);
            load();
          }
        })
        .catch(function () { /* cart unreadable: leave the hold alone, TTL covers it */ });
      return true;
    }

    /* ---- go -------------------------------------------------------------- */
    if (!restore()) load();

    // Keep the browsed calendar honest while the form sits open. The shared
    // focus/visibility listener (registerWidget) covers "tabbed away and back";
    // this timer covers a customer who simply lingers. Both funnel through
    // maybeRevalidate, which no-ops unless the form is actually on screen. The
    // timer self-terminates once its widget leaves the DOM.
    registerWidget(root, maybeRevalidate);
    S.reval = setInterval(function () {
      if (!document.contains(root)) { clearInterval(S.reval); S.reval = null; return; }
      maybeRevalidate('interval');
    }, REVALIDATE_MS);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  // Theme editor re-renders a section on every settings change.
  document.addEventListener('shopify:section:load', function (e) { init((e && e.target) || document); });
})();
